import { V3, v3, rng } from './math';

/**
 * Props geometry: position, normal, colour, material — 10 floats a vertex. Materials pick a pattern in the prop shader.
 */
export const MAT = {
  plain: 0, plank: 1, siding: 2, door: 3, plastic: 4, stem: 5, wax: 6, metal: 7, window: 8, soil: 9, leaf: 10, stone: 11,
  glow: 12, roof: 13, flame: 14, brass: 15, glass: 16, filament: 17, rail: 18, post: 19, granite: 20,
} as const;

export class Builder {
  v: number[] = [];
  i: number[] = [];
  get count() { return this.v.length / 10; }

  vert(p: V3, n: V3, c: V3, mat: number): number {
    this.v.push(p[0], p[1], p[2], n[0], n[1], n[2], c[0], c[1], c[2], mat);
    return this.count - 1;
  }

  tri(a: number, b: number, c: number) { this.i.push(a, b, c); }

  quad(a: V3, b: V3, c: V3, d: V3, col: V3, mat: number) {
    const n = v3.norm(v3.cross(v3.sub(b, a), v3.sub(d, a)));
    const ia = this.vert(a, n, col, mat), ib = this.vert(b, n, col, mat), ic = this.vert(c, n, col, mat), id = this.vert(d, n, col, mat);
    this.tri(ia, ib, ic); this.tri(ia, ic, id);
  }

  /**
   * a box centred on `c`, half-sizes `h`, turned by `yaw` about y and tipped by `tilt` (x then z, radians), each corner
   * nudged by up to `wob` metres: the jank that keeps it from looking machined
   */
  box(c: V3, h: V3, col: V3, mat: number, o: { yaw?: number; tilt?: [number, number]; wob?: number; seed?: number; skip?: string } = {}) {
    const r = rng(o.seed ?? 1), wob = o.wob ?? 0;
    const cy = Math.cos(o.yaw ?? 0), sy = Math.sin(o.yaw ?? 0);
    const [tx, tz] = o.tilt ?? [0, 0];
    const corner = (sx: number, sy2: number, sz: number): V3 => {
      let x = sx * h[0] + (r() - 0.5) * 2 * wob, y = sy2 * h[1] + (r() - 0.5) * 2 * wob, z = sz * h[2] + (r() - 0.5) * 2 * wob;
      // tip about x, then z
      let y1 = y * Math.cos(tx) - z * Math.sin(tx); z = y * Math.sin(tx) + z * Math.cos(tx); y = y1;
      const x1 = x * Math.cos(tz) - y * Math.sin(tz); y = x * Math.sin(tz) + y * Math.cos(tz); x = x1;
      return [c[0] + x * cy + z * sy, c[1] + y, c[2] - x * sy + z * cy];
    };
    const k: V3[] = [];
    for (let i = 0; i < 8; i++) k.push(corner(i & 1 ? 1 : -1, i & 2 ? 1 : -1, i & 4 ? 1 : -1));
    const skip = o.skip ?? '';
    // faces: +x -x +y -y +z -z, corners anticlockwise from outside
    if (!skip.includes('x+')) this.quad(k[1], k[3], k[7], k[5], col, mat);
    if (!skip.includes('x-')) this.quad(k[4], k[6], k[2], k[0], col, mat);
    if (!skip.includes('y+')) this.quad(k[2], k[6], k[7], k[3], col, mat);
    if (!skip.includes('y-')) this.quad(k[0], k[1], k[5], k[4], col, mat);
    if (!skip.includes('z+')) this.quad(k[4], k[5], k[7], k[6], col, mat);
    if (!skip.includes('z-')) this.quad(k[0], k[2], k[3], k[1], col, mat);
  }

  /** a surface of revolution about the y axis through `at`: profile [radius, y] from bottom to top, smooth normals */
  lathe(at: V3, profile: [number, number][], col: (k: number, j: number) => V3, mat: number, seg = 20, ridge?: (a: number) => number) {
    const base = this.count, n = profile.length;
    for (let k = 0; k < n; k++) {
      const [r, y] = profile[k];
      const [r0, y0] = profile[Math.max(0, k - 1)], [r1, y1] = profile[Math.min(n - 1, k + 1)];
      const dr = r1 - r0, dy = y1 - y0;
      for (let j = 0; j <= seg; j++) {
        const a = j / seg * Math.PI * 2, rr = r * (ridge ? ridge(a) : 1);
        const p: V3 = [at[0] + Math.cos(a) * rr, at[1] + y, at[2] + Math.sin(a) * rr];
        const nrm = v3.norm([Math.cos(a) * dy, -dr, Math.sin(a) * dy]);
        this.vert(p, nrm, col(k, j), mat);
      }
    }
    for (let k = 0; k < n - 1; k++) for (let j = 0; j < seg; j++) {
      const a = base + k * (seg + 1) + j, b = a + seg + 1;
      this.tri(a, b, a + 1); this.tri(a + 1, b, b + 1);
    }
  }

  /** a tube along `spine`, radius per point, `sides` round; capped at the end */
  tube(spine: V3[], radius: number[], col: (k: number, j: number) => V3, mat: number, sides = 10, ridge?: (a: number) => number) {
    const base = this.count, n = spine.length;
    let ref: V3 = [1, 0, 0];
    const frames: [V3, V3, V3][] = [];
    for (let k = 0; k < n; k++) {
      const t = v3.norm(v3.sub(spine[Math.min(n - 1, k + 1)], spine[Math.max(0, k - 1)]));
      let b = v3.cross(t, ref);
      if (v3.len(b) < 1e-4) b = v3.cross(t, [0, 0, 1]);
      b = v3.norm(b);
      const nn = v3.cross(b, t);
      ref = nn;
      frames.push([t, nn, b]);
    }
    for (let k = 0; k < n; k++) {
      const [, nn, b] = frames[k];
      for (let j = 0; j <= sides; j++) {
        const a = j / sides * Math.PI * 2, rr = radius[k] * (ridge ? ridge(a) : 1);
        const d = v3.add(v3.scale(nn, Math.cos(a)), v3.scale(b, Math.sin(a)));
        this.vert(v3.add(spine[k], v3.scale(d, rr)), d, col(k, j), mat);
      }
    }
    for (let k = 0; k < n - 1; k++) for (let j = 0; j < sides; j++) {
      const a = base + k * (sides + 1) + j, c = a + sides + 1;
      this.tri(a, a + 1, c); this.tri(a + 1, c + 1, c);
    }
    const end = spine[n - 1], t = frames[n - 1][0], cap = this.vert(end, t, v3.scale(col(n - 1, 0), 0.7), mat);
    for (let j = 0; j < sides; j++) {
      const a = base + (n - 1) * (sides + 1) + j;
      this.tri(a, cap, a + 1);
    }
  }

  data() { return { v: new Float32Array(this.v), i: new Uint32Array(this.i) }; }
}
